import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import api from '../../../services/api';

export interface DashboardGroup {
  id: string;
  title: string;
}

export interface WidgetConfig {
  id: string;
  widgetType: string;
  position: { x: number; y: number };
  size: { w: number; h: number };
  settings?: Record<string, unknown>;
  order: number;
  groupId?: string;
}

export interface DashboardLayoutResponse {
  id: number;
  userId: number;
  name: string;
  isDefault: boolean;
  widgets: WidgetConfig[];
  groups?: DashboardGroup[] | null;
  createdAt: string;
  updatedAt: string;
}

export function useDashboardLayout(layoutName?: string) {
  const url = layoutName ? `/dashboard/layout?name=${encodeURIComponent(layoutName)}` : '/dashboard/layout';
  const { data, error, isLoading, mutate } = useSWR<DashboardLayoutResponse>(url, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    revalidateIfStale: true,
  });
  return { layout: data, isLoading, isError: error, mutate };
}

export function useDashboardLayouts() {
  const { data, error, isLoading, mutate } = useSWR<DashboardLayoutResponse[]>('/dashboard/layouts', fetcher, {
    revalidateOnFocus: true,
  });
  return { layouts: data ?? [], isLoading, isError: error, mutate };
}

export function useSaveDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (payload: { name?: string; isDefault?: boolean; widgets: WidgetConfig[]; groups?: DashboardGroup[] }) => {
    const res = await api.post('/dashboard/layout', payload);
    await Promise.all([mutateLayout(), mutateLayouts()]);
    return res.data;
  };
}

export function useUpdateDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number, payload: { name?: string; isDefault?: boolean; widgets?: WidgetConfig[]; groups?: DashboardGroup[] }) => {
    const res = await api.put(`/dashboard/layout/${layoutId}`, payload);
    await Promise.all([mutateLayout(), mutateLayouts()]);
    return res.data;
  };
}

export function useDeleteDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number) => {
    await api.delete(`/dashboard/layout/${layoutId}`);
    await Promise.all([mutateLayout(), mutateLayouts()]);
  };
}

export function useSetDefaultLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number) => {
    await api.put(`/dashboard/layout/${layoutId}/default`);
    await Promise.all([mutateLayout(), mutateLayouts()]);
  };
}
